// src/pages/ShopManager.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const SHOPS_API =
    "https://oppiesfunc.azurewebsites.net/api/getAllShops?code=Dx_kliFsd4-5j40_pt5i6RsR36dv01K92JHMPb6eQRHBAzFuQKNV3A==";
const DELETE_API =
    "https://oppiesfunc.azurewebsites.net/api/deleteShop?code=IY5Wjkwpx9ZYDek6J5j6Mb_HqKzolY13tFDXybz22ezMAzFulJLWUw==";
const UPDATE_API =
    "https://oppiesfunc.azurewebsites.net/api/updateShopDetails?code=eJ8ws1JkPw9R-0JihVCq6FBVe7LvtiI7ZzXuxLV58CqhAzFuV8Yrvg==";

const OPPIES_BEIGE = "#bfa97c";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function ShopManager() {
    const [shops, setShops] = useState([]);
    const [loading, setLoading] = useState(true);

    const [status, setStatus] = useState({ message: "", isError: false });
    const [search, setSearch] = useState("");

    // UI state
    const [expandedId, setExpandedId] = useState(null); // which card is open
    const [editId, setEditId] = useState(null); // which card is editing
    const [busyId, setBusyId] = useState(null); // which shop is saving/deleting

    // Local edits (draft copy by shopId)
    const [drafts, setDrafts] = useState({}); // { [shopId]: draftShop }

    const fetchShops = async () => {
        setLoading(true);
        setStatus({ message: "", isError: false });
        try {
            const res = await axios.get(SHOPS_API, { timeout: 20000 });
            const list = Array.isArray(res.data) ? res.data : [];

            // Stable sort by name
            const sorted = [...list].sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
            setShops(sorted);
        } catch (err) {
            console.error("❌ Failed to load shops:", err?.message || err);
            setStatus({ message: "Failed to load shops.", isError: true });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchShops();
    }, []);

    const filteredShops = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return shops;

        return shops.filter((s) => {
            const hay = `${s?.name || ""} ${s?.shopId || ""} ${s?.email || ""}`.toLowerCase();
            return hay.includes(term);
        });
    }, [shops, search]);

    const toggleExpand = (shopId) => {
        setStatus({ message: "", isError: false });
        setExpandedId((prev) => (prev === shopId ? null : shopId));

        // If opening, ensure we have a draft loaded
        setDrafts((prev) => {
            if (prev[shopId]) return prev;
            const found = shops.find((x) => x.shopId === shopId);
            return found ? { ...prev, [shopId]: deepCloneShop(found) } : prev;
        });
    };

    const startEdit = (shopId) => {
        setStatus({ message: "", isError: false });
        setEditId(shopId);

        // Reset draft from latest shop data when editing starts
        const found = shops.find((x) => x.shopId === shopId);
        if (found) {
            setDrafts((prev) => ({ ...prev, [shopId]: deepCloneShop(found) }));
        }
    };

    const cancelEdit = (shopId) => {
        setEditId(null);
        const found = shops.find((x) => x.shopId === shopId);
        if (found) setDrafts((prev) => ({ ...prev, [shopId]: deepCloneShop(found) }));
    };

    const updateDraftField = (shopId, field, value) => {
        setDrafts((prev) => ({
            ...prev,
            [shopId]: {
                ...(prev[shopId] || {}),
                [field]: value,
            },
        }));
    };

    const updateDraftHour = (shopId, day, value) => {
        setDrafts((prev) => ({
            ...prev,
            [shopId]: {
                ...(prev[shopId] || {}),
                hours: {
                    ...((prev[shopId] && prev[shopId].hours) || {}),
                    [day]: value,
                },
            },
        }));
    };

    const handleSave = async (shopId) => {
        const draft = drafts[shopId];
        if (!draft) return;

        setBusyId(shopId);
        setStatus({ message: "", isError: false });

        try {
            await axios.post(
                UPDATE_API,
                {
                    shopId: draft.shopId,
                    name: (draft.name || "").trim(),
                    email: (draft.email || "").trim(),
                    address: (draft.address || "").trim(),
                    contact: (draft.contact || "").trim(),
                    hours: draft.hours || null,
                },
                { timeout: 20000 }
            );

            // Update local list
            setShops((prev) =>
                prev.map((s) => (s.shopId === shopId ? { ...s, ...draft } : s))
            );

            setStatus({ message: `✅ Updated "${draft.name || shopId}"`, isError: false });
            setEditId(null);
        } catch (err) {
            console.error("❌ Update error:", err?.message || err);
            const apiMsg = err?.response?.data?.error || err?.message || "Failed to update shop.";
            setStatus({ message: apiMsg, isError: true });
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (shopId) => {
        const shop = shops.find((x) => x.shopId === shopId);
        const label = shop?.name || shopId;

        if (!window.confirm(`Delete shop "${label}"?\n\nThis cannot be undone.`)) return;

        setBusyId(shopId);
        setStatus({ message: "", isError: false });

        try {
            await axios.post(DELETE_API, { shopId }, { timeout: 20000 });

            setShops((prev) => prev.filter((s) => s.shopId !== shopId));

            if (expandedId === shopId) setExpandedId(null);
            if (editId === shopId) setEditId(null);

            setStatus({ message: `🗑️ Deleted "${label}"`, isError: false });
        } catch (err) {
            console.error("❌ Delete error:", err?.message || err);
            const apiMsg = err?.response?.data?.error || err?.message || "Failed to delete shop.";
            setStatus({ message: apiMsg, isError: true });
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.headerRow}>
                <div>
                    <h2 style={styles.heading}>Op Shops</h2>
                    <div style={styles.subheading}>Search, view, edit, or remove approved shops.</div>
                </div>

                <button className="btn-secondary" style={{ height: 38 }} onClick={fetchShops}>
                    Refresh
                </button>
            </div>

            <div style={styles.searchWrap}>
                <input
                    type="text"
                    placeholder="Search by name, shopId, or email…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={styles.searchInput}
                />
                <div style={styles.searchMeta}>
                    Showing <strong>{filteredShops.length}</strong> of <strong>{shops.length}</strong>
                </div>
            </div>

            {status.message && (
                <div
                    className="card"
                    style={{
                        marginBottom: 14,
                        borderColor: status.isError ? "#ffcccc" : "#d9f2e3",
                        background: status.isError ? "#fff6f6" : "#f4fff8",
                    }}
                >
                    <div style={{ fontWeight: 800, color: status.isError ? "#a94442" : "#1f5130" }}>
                        {status.message}
                    </div>
                </div>
            )}

            {loading ? (
                <div className="card" style={{ textAlign: "center" }}>
                    Loading shops…
                </div>
            ) : filteredShops.length === 0 ? (
                <div className="card" style={{ textAlign: "center" }}>
                    No matching shops.
                </div>
            ) : (
                <div style={styles.list}>
                    {filteredShops.map((shop) => {
                        const id = shop.shopId;
                        const open = expandedId === id;
                        const editing = editId === id;
                        const busy = busyId === id;

                        const draft = drafts[id] || shop;

                        return (
                            <div key={id} className="card" style={styles.card}>
                                {/* Collapsed header */}
                                <button
                                    onClick={() => toggleExpand(id)}
                                    style={styles.cardTopButton}
                                    aria-expanded={open}
                                >
                                    <div style={styles.cardTopLeft}>
                                        <div style={styles.shopNameRow}>
                                            <div style={styles.shopName}>{shop.name || "(Unnamed shop)"}</div>
                                            <span style={styles.pill}>{shop.town || "Op Shop"}</span>
                                        </div>
                                        <div style={styles.shopMeta}>
                                            <span><strong>ID:</strong> {shop.shopId}</span>
                                            {shop.email ? <span>• <strong>Email:</strong> {shop.email}</span> : null}
                                        </div>
                                    </div>

                                    <div style={styles.expandIcon}>{open ? "−" : "+"}</div>
                                </button>

                                {/* Expanded panel */}
                                {open && (
                                    <div style={styles.panel}>
                                        {!editing ? (
                                            <>
                                                <div style={styles.detailGrid}>
                                                    <InfoRow label="Address" value={shop.address || "—"} />
                                                    <InfoRow label="Contact" value={shop.contact || "—"} />
                                                    <InfoRow
                                                        label="Opening hours"
                                                        value={<HoursView hours={shop.hours} />}
                                                    />
                                                </div>

                                                <div style={styles.actionRow}>
                                                    <button
                                                        className="btn-primary"
                                                        onClick={() => startEdit(id)}
                                                        disabled={busy}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="btn-danger"
                                                        onClick={() => handleDelete(id)}
                                                        disabled={busy}
                                                    >
                                                        {busy ? "Working…" : "Delete"}
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div style={styles.detailGrid}>
                                                    <Field
                                                        label="Name"
                                                        value={draft.name || ""}
                                                        onChange={(v) => updateDraftField(id, "name", v)}
                                                    />
                                                    <Field
                                                        label="Email"
                                                        value={draft.email || ""}
                                                        onChange={(v) => updateDraftField(id, "email", v)}
                                                    />
                                                    <Field
                                                        label="Address"
                                                        value={draft.address || ""}
                                                        onChange={(v) => updateDraftField(id, "address", v)}
                                                    />
                                                    <Field
                                                        label="Contact"
                                                        value={draft.contact || ""}
                                                        onChange={(v) => updateDraftField(id, "contact", v)}
                                                    />

                                                    <div style={styles.hoursBlock}>
                                                        <div style={styles.fieldLabel}>Opening Hours</div>
                                                        <div style={styles.hoursGrid}>
                                                            {DAYS.map((day) => (
                                                                <div key={day} style={styles.hourRow}>
                                                                    <div style={styles.hourDay}>{day}</div>
                                                                    <input
                                                                        value={(draft.hours && draft.hours[day]) || ""}
                                                                        onChange={(e) => updateDraftHour(id, day, e.target.value)}
                                                                        placeholder="e.g. 9am - 5pm"
                                                                        style={styles.hourInput}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div style={styles.actionRow}>
                                                    <button
                                                        className="btn-primary"
                                                        onClick={() => handleSave(id)}
                                                        disabled={busy}
                                                    >
                                                        {busy ? "Saving…" : "Save"}
                                                    </button>
                                                    <button className="btn-secondary" onClick={() => cancelEdit(id)} disabled={busy}>
                                                        Cancel
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div style={styles.infoRow}>
            <div style={styles.infoLabel}>{label}</div>
            <div style={styles.infoValue}>{value}</div>
        </div>
    );
}

function Field({ label, value, onChange }) {
    return (
        <div style={styles.field}>
            <div style={styles.fieldLabel}>{label}</div>
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                style={styles.input}
            />
        </div>
    );
}

function HoursView({ hours }) {
    if (!hours) return <span style={{ fontStyle: "italic", opacity: 0.75 }}>Not set</span>;
    const entries = Object.entries(hours);
    if (!entries.length) return <span style={{ fontStyle: "italic", opacity: 0.75 }}>Not set</span>;

    return (
        <div style={{ display: "grid", gap: 6 }}>
            {entries.map(([day, val]) => (
                <div key={day} style={{ display: "flex", gap: 8 }}>
                    <div style={{ width: 90, fontWeight: 700 }}>{day}</div>
                    <div style={{ opacity: 0.9 }}>{val || "—"}</div>
                </div>
            ))}
        </div>
    );
}

function deepCloneShop(shop) {
    return {
        ...shop,
        hours: shop?.hours ? { ...shop.hours } : null,
    };
}

const styles = {
    container: {
        padding: 10,
        width: "100%",
        fontFamily: "Nunito, sans-serif",
    },

    headerRow: {
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 14,
    },

    heading: { fontSize: 26, color: "#5c4a3b", margin: 0, letterSpacing: 0.4 },
    subheading: { marginTop: 4, color: "#857969", fontSize: 13 },

    searchWrap: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginBottom: 14,
        flexWrap: "wrap",
    },
    searchInput: {
        flex: 1,
        minWidth: 280,
        padding: "10px 12px",
        borderRadius: 12,
        border: "1px solid #e1d9cd",
        fontSize: 14,
        background: "#fff",
    },
    searchMeta: { color: "#857969", fontSize: 13 },

    list: { display: "grid", gap: 12 },

    card: {
        border: "1px solid #e7e0d6",
        borderRadius: 14,
        background: "#fff",
    },

    cardTopButton: {
        width: "100%",
        border: "none",
        background: "transparent",
        textAlign: "left",
        padding: 14,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cardShadow: "none",
        cursor: "pointer",
    },

    cardTopLeft: { display: "flex", flexDirection: "column", gap: 6 },

    shopNameRow: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
    shopName: { fontWeight: 900, color: "#3c3329", fontSize: 16 },

    pill: {
        fontSize: 12,
        padding: "3px 10px",
        borderRadius: 999,
        background: "#f5f0e1",
        color: "#6b5b3a",
        border: `1px solid ${OPPIES_BEIGE}`,
        fontWeight: 800,
    },

    shopMeta: { fontSize: 13, color: "#6f6357", display: "flex", gap: 8, flexWrap: "wrap" },

    expandIcon: {
        width: 34,
        height: 34,
        borderRadius: 10,
        border: "1px solid #e7e0d6",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: OPPIES_BEIGE,
        fontWeight: 900,
        fontSize: 18,
        background: "#fffaf2",
    },

    panel: {
        borderTop: "1px solid #eee7de",
        padding: 14,
        background: "#fffaf2",
        borderBottomLeftRadius: 14,
        borderBottomRightRadius: 14,
    },

    detailGrid: { display: "grid", gap: 12 },

    infoRow: {
        padding: 12,
        background: "#fff",
        borderRadius: 12,
        border: "1px solid #efe7dd",
    },
    infoLabel: { fontSize: 12, color: "#857969", textTransform: "uppercase", letterSpacing: 0.5 },
    infoValue: { marginTop: 6, color: "#3c3329", fontSize: 14 },

    actionRow: { display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" },

    field: {
        padding: 12,
        background: "#fff",
        borderRadius: 12,
        border: "1px solid #efe7dd",
    },
    fieldLabel: { fontSize: 12, color: "#857969", textTransform: "uppercase", letterSpacing: 0.5 },
    input: {
        marginTop: 8,
        width: "100%",
        padding: "10px 10px",
        borderRadius: 10,
        border: "1px solid #e1d9cd",
        fontSize: 14,
        outline: "none",
    },

    hoursBlock: {
        padding: 12,
        background: "#fff",
        borderRadius: 12,
        border: "1px solid #efe7dd",
    },
    hoursGrid: { marginTop: 10, display: "grid", gap: 8 },
    hourRow: { display: "grid", gridTemplateColumns: "110px 1fr", gap: 10, alignItems: "center" },
    hourDay: { fontSize: 13, color: "#3c3329", fontWeight: 800 },
    hourInput: {
        padding: "9px 10px",
        borderRadius: 10,
        border: "1px solid #e1d9cd",
        fontSize: 14,
        width: "100%",
    },
};
